import { yupResolver } from '@hookform/resolvers/yup'
import { Scan } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { PageLayout } from '@/components'
import { Card, CardContent } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { secondsToMinutes } from '../../../core/constants/times'
import { validateForgotPasswordCode } from '../../services/auth'
import { FeatureCarousel } from '../FeatureCarousel'

interface SMSCodeValues {
  code: string
}

interface LocationState {
  phone: string
}

const ResetPasswordPage: React.FC = () => {
  const navigation = useNavigate()
  const location = useLocation()
  const [counter, setCounter] = useState(600)
  const [wrongCode, setWrongCode] = useState<boolean>(false)

  // Try to get phone from location state, fallback to sessionStorage
  const phoneFromState = (location.state as LocationState)?.phone
  const phoneFromStorage = sessionStorage.getItem('reset_password_phone')
  const phone = phoneFromState || phoneFromStorage || ''

  useEffect(() => {
    if (!phone) {
      console.warn('No phone number found, redirecting to forgot-password')
      navigation('/account/forgot-password')
    }
  }, [phone, navigation])

  const schema = yup.object().shape({
    code: yup
      .string()
      .required('SMS Code is required')
      .length(6, 'SMS Code must be 6 digits'),
  })

  useEffect(() => {
    if (counter > 0) {
      setTimeout(() => setCounter(counter - 1), 1000)
    }
  }, [counter])

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<SMSCodeValues>({
    mode: 'onChange',
    resolver: yupResolver<SMSCodeValues>(schema),
  })

  const submit = async (data: SMSCodeValues) => {
    const response = await validateForgotPasswordCode(phone, data.code)
    if (response.data.valid) {
      // Store in sessionStorage as backup for new-password page
      sessionStorage.setItem('new_password_phone', phone)
      sessionStorage.setItem(
        'new_password_validation_code',
        response.data.validationCode,
      )

      // Clear reset password phone from storage
      sessionStorage.removeItem('reset_password_phone')

      navigation(`/account/new-password`, {
        state: {
          phone,
          validationCode: response.data.validationCode,
        },
      })
    } else {
      setWrongCode(true)
    }
  }

  const features = [
    {
      icon: (
        <div className="w-16 h-16 rounded-2xl bg-accent-1-60 flex items-center justify-center">
          <Scan className="w-8 h-8 text-accent-1" />
        </div>
      ),
      title: 'Fast & Secure Check-In',
      description:
        'Say goodbye to paper tickets and manual lists. Our blockchain-powered check-in system allows for instant ticket validation—whether scanned at the door or verified on-chain.',
    },
  ]

  return (
    <PageLayout authPage>
      <div className="container flex mt-12">
        {/* Left Side - Features Section */}
        <div className="relative w-1/2 flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <FeatureCarousel features={features} />
          </div>
        </div>

        {/* Right Side - Form */}
        <Card className="w-[527px]">
          <CardContent className="flex flex-col gap-5 items-center w-full px-24 py-16">
            <div className="w-full">
              <div className="text-title-m text-center mb-2">
                Reset Password
              </div>
              <div className="text-body-medium text-monochrome-2 text-center mb-8">
                {`Enter the 6 digits code we've sent you to ${phone}`}
              </div>

              <form onSubmit={handleSubmit(submit)} className="space-y-7">
                <div className="space-y-2">
                  <div className="flex justify-center">
                    <Controller
                      name="code"
                      control={control}
                      render={({ field }) => (
                        <InputOTP
                          maxLength={6}
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                  </div>
                  {errors.code && (
                    <p className="text-sm text-destructive text-center">
                      {errors.code.message}
                    </p>
                  )}
                </div>

                {wrongCode && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      SMS Code is not correct, you can resend your SMS Code
                      after timer below expires
                    </AlertDescription>
                  </Alert>
                )}

                <p className="text-body-medium text-monochrome-2">
                  {`You can resend your code in ${secondsToMinutes(counter)}`}
                </p>

                <Button
                  className="w-full"
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Continue
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}

export default ResetPasswordPage
